// Tiny SVG audio waveform used as a divider between nav clusters in the
// header (replaces the boring `│` vertical pipe). Keeps the theme honest
// to "producer tool" instead of generic SaaS. Stroke-only, accent-colored,
// ~24x16 by default. Pure presentational - no semantics, hidden from
// screen readers via aria-hidden.

type Props = {
  // Total width in px. Height stays fixed at 16. More width = more samples.
  width?: number;
  // Tailwind text-color class controlling the stroke. Default uses border
  // muted so it sits at the same visual weight as a real divider.
  className?: string;
};

// Pre-generated, deterministic waveform amplitudes so SSR/CSR match and the
// shape never changes between renders. Designed to look like a short bar of
// pcm audio (mostly low, occasional spikes). Values in [-1, 1].
const SAMPLES = [
  0.15, -0.2, 0.35, -0.45, 0.6, -0.55, 0.4, -0.25, 0.15, -0.35, 0.55, -0.65, 0.5, -0.3, 0.2, -0.15,
];

export function WaveformDivider({ width = 28, className = 'text-border' }: Props) {
  const h = 16;
  const mid = h / 2;
  const step = width / (SAMPLES.length - 1);
  const path = SAMPLES.map(
    (amp, i) =>
      `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${(mid + amp * (mid - 2)).toFixed(2)}`,
  ).join(' ');
  return (
    <svg
      width={width}
      height={h}
      viewBox={`0 0 ${width} ${h}`}
      role="presentation"
      aria-hidden="true"
      className={`shrink-0 ${className}`}
    >
      <title>divider</title>
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}
